import React from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Tabs from '@amzn/awsui-components-console/tabs';
import Spinner from '@amzn/awsui-components-console/spinner';
import CollectionCard from './CollectionCard';
import CollectionCardSkeleton from './CollectionCardSkeleton';
import EmptyState from '../common/EmptyState';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import type { DataSourceTab } from '../../types';
import './DataSourceDetails.scss';

export interface DataSourceDetailsProps {
  tabs: DataSourceTab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  collections: Collection[];
  loading: boolean;
  onCollectionClick: (id: string) => void;
}

const DataSourceDetails: React.FC<DataSourceDetailsProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  collections,
  loading,
  onCollectionClick,
}) => {
  const navigate = useNavigate();
  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const dataSourceNumber = tabs.findIndex((tab) => tab.id === activeTabId) + 1;

  const handleStatsClick = () => {
    if (activeTab?.label) {
      navigate(`/collections?source=${encodeURIComponent(activeTab.label)}`);
    }
  };

  return (
    <div className="data-source-details-wrapper">
      <Container>
        {loading && tabs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spinner size="large" /> Loading collections...
          </div>
        ) : (
          <Tabs
            activeTabId={activeTabId}
            onChange={({ detail }) => onTabChange(detail.activeTabId)}
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              content: (
                <div className="data-source-content">
                  <Header variant="h1">
                    Data Source {dataSourceNumber} - {activeTab?.label}
                  </Header>

                  <div className="data-source-stats">
                    <span onClick={handleStatsClick} style={{ cursor: 'pointer' }}>
                      {activeTab?.collectionsCount} {activeTab?.collectionsCount === 1 ? 'Collection' : 'Collections'},{' '}
                      {activeTab?.itemsCount.toLocaleString()} Items
                    </span>
                  </div>

                  {loading ? (
                    <>
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <Spinner size="normal" /> Loading collections...
                      </div>
                      <div className="collections-grid">
                        {[...Array(6)].map((_, index) => (
                          <CollectionCardSkeleton key={index} />
                        ))}
                      </div>
                    </>
                  ) : collections.length === 0 ? (
                    <EmptyState title="No collections found" description="There are no collections for this data source." icon="folder" />
                  ) : (
                    <div className="collections-grid">
                      {collections.map((collection) => (
                        <CollectionCard key={collection.collectionId} collection={collection} onClick={onCollectionClick} />
                      ))}
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Container>
    </div>
  );
};

export default DataSourceDetails;
